"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback, useMemo, forwardRef, useEffect, } from "react";
import { Box, Flex, Text, List, ListItem, useOutsideClick, } from "@chakra-ui/react";
import { matchSorter } from "match-sorter";
import useDebouncedCallback from "beautiful-react-hooks/useDebouncedCallback";
import reactStringReplace from "react-string-replace";
import Input from "../../atoms/Input/Input";
import { KeyboardCodes } from "../../helpers";
import SvgSpriteConsumer from "../../atoms/SvgSprite/SvgSpriteConsumer";
import { IconButton } from "../../atoms/IconButton";
export const defaultHighlightSearchedWords = (data, { inputValue, }) => {
    const label = inputValue !== ""
        ? reactStringReplace(data.label, new RegExp(`(${inputValue})`, "gi"), (match, i) => (_jsx(Text, Object.assign({ as: "span", variant: "body.mediumXbold" }, { children: match }), i)))
        : data.label;
    return (_jsx(Text, Object.assign({ variant: "body.mediumDemiBold", as: "span" }, { children: label })));
};
const AutoSuggest = forwardRef((_a, ref) => {
    var { options, notFoundText = "Not found", onInputChange, onInputChangeDebounceTime, onInputSubmit, onOptionSelect, onClick, onFocus, onBlur, onKeyDown, suggestionMaxNumber = 10, clearable, alwaysDisplayOptions = false, closeOnSubmit = false, onInputClear, initialValue, highlightSearchedWords = defaultHighlightSearchedWords, renderOption, filterKey = "label" } = _a, rest = __rest(_a, ["options", "notFoundText", "onInputChange", "onInputChangeDebounceTime", "onInputSubmit", "onOptionSelect", "onClick", "onFocus", "onBlur", "onKeyDown", "suggestionMaxNumber", "clearable", "alwaysDisplayOptions", "closeOnSubmit", "onInputClear", "initialValue", "highlightSearchedWords", "renderOption", "filterKey"]);
    const [inputValue, setInputValue] = useState(initialValue || null);
    const [areOptionsDisplayed, setAreOptionsDisplayed] = useState(false);
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
    const containerRef = useRef(null);
    useOutsideClick({
        ref: containerRef,
        handler: () => {
            setAreOptionsDisplayed(false);
            setFocusedOptionIndex(null);
        },
    });
    const debouncedOnInputChange = useDebouncedCallback((val) => {
        onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(val);
    }, [onInputChange], onInputChangeDebounceTime !== null && onInputChangeDebounceTime !== void 0 ? onInputChangeDebounceTime : 0);
    const filteredOptions = useMemo(() => {
        if (!inputValue) {
            return options.slice(0, suggestionMaxNumber);
        }
        return matchSorter(options, inputValue, {
            keys: [{ threshold: matchSorter.rankings.CONTAINS, key: filterKey }],
        }).slice(0, suggestionMaxNumber);
    }, [inputValue, options, suggestionMaxNumber, filterKey]);
    const setOptionsDisplayWithValue = useCallback((value) => {
        if (value || alwaysDisplayOptions) {
            setAreOptionsDisplayed(true);
        }
        else {
            setAreOptionsDisplayed(false);
        }
    }, [alwaysDisplayOptions]);
    useEffect(() => {
        setInputValue(initialValue);
    }, [initialValue]);
    useEffect(() => {
        if (inputValue === "") {
            onInputClear === null || onInputClear === void 0 ? void 0 : onInputClear();
            if (!alwaysDisplayOptions) {
                setAreOptionsDisplayed(false);
            }
        }
    }, [alwaysDisplayOptions, inputValue, onInputClear]);
    return (_jsxs(Box, Object.assign({ width: "100%", position: "relative", ref: containerRef }, { children: [_jsx(Input, Object.assign({ rightElement: clearable && inputValue ? (_jsx(Box, Object.assign({ display: "flex", alignItems: "center", justifyContent: "center", width: "1.5rem", height: "1.5rem" }, { children: _jsx(IconButton, { onMouseDown: (e) => {
                            e.preventDefault();
                        }, onClick: () => {
                            setInputValue("");
                        }, "aria-label": "clear input", variant: "icon", icon: _jsx(SvgSpriteConsumer, { color: "semantic.icon.primary", svgId: "delete24", size: 24 }) }) }))) : null, onBlur: (e) => {
                    setAreOptionsDisplayed(false);
                    setFocusedOptionIndex(null);
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                }, onClick: (e) => {
                    onClick === null || onClick === void 0 ? void 0 : onClick(e);
                    setOptionsDisplayWithValue(e.currentTarget.value);
                }, onFocus: (e) => {
                    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
                    setOptionsDisplayWithValue(e.currentTarget.value);
                }, onKeyDown: (e) => {
                    if (e.key === KeyboardCodes.ENTER) {
                        if (filteredOptions && focusedOptionIndex !== null) {
                            const option = filteredOptions[focusedOptionIndex];
                            setInputValue(option.label);
                            setAreOptionsDisplayed(false);
                            onOptionSelect === null || onOptionSelect === void 0 ? void 0 : onOptionSelect(option);
                            setFocusedOptionIndex(null);
                        }
                        else {
                            onInputSubmit === null || onInputSubmit === void 0 ? void 0 : onInputSubmit(e.currentTarget.value);
                            if (closeOnSubmit && !alwaysDisplayOptions) {
                                setAreOptionsDisplayed(false);
                            }
                        }
                    }
                    if (areOptionsDisplayed &&
                        (filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) &&
                        e.key === KeyboardCodes.ARROW_DOWN) {
                        setFocusedOptionIndex((previousFocusedOptionIndex) => {
                            if (previousFocusedOptionIndex === null) {
                                return 0;
                            }
                            if (filteredOptions[previousFocusedOptionIndex + 1]) {
                                return previousFocusedOptionIndex + 1;
                            }
                            return 0;
                        });
                    }
                    if (areOptionsDisplayed &&
                        (filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) &&
                        e.key === KeyboardCodes.ARROW_UP) {
                        setFocusedOptionIndex((previousFocusedOptionIndex) => {
                            if (previousFocusedOptionIndex === null) {
                                return null;
                            }
                            if (filteredOptions[previousFocusedOptionIndex - 1]) {
                                return previousFocusedOptionIndex - 1;
                            }
                            return filteredOptions.length - 1;
                        });
                    }
                    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
                }, onChange: (e) => {
                    setInputValue(e.currentTarget.value);
                    setOptionsDisplayWithValue(e.currentTarget.value);
                    debouncedOnInputChange(e.currentTarget.value);
                }, ref: ref, value: inputValue !== null && inputValue !== void 0 ? inputValue : "" }, rest)), areOptionsDisplayed && (_jsxs(List, Object.assign({ variant: "autoSuggest" }, { children: [filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.map((option, optionIndex) => (_createElement(ListItem, Object.assign({}, (renderOption
                        ? {
                            height: "auto",
                            padding: 0,
                        }
                        : {}), { key: option.value, onMouseDown: (e) => {
                            // important to not trigger input blur when clicking on an option
                            e.preventDefault();
                            e.stopPropagation();
                        }, onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setInputValue(option.label);
                            setAreOptionsDisplayed(false);
                            onOptionSelect === null || onOptionSelect === void 0 ? void 0 : onOptionSelect(option);
                            setFocusedOptionIndex(null);
                        } }, (optionIndex === focusedOptionIndex
                        ? { className: "ds-autosuggest-focused-option" }
                        : {})),
                        !renderOption && (_jsx(Flex, Object.assign({ align: "center" }, { children: highlightSearchedWords(option, {
                                inputValue: inputValue !== null && inputValue !== void 0 ? inputValue : "",
                            }) }))),
                        renderOption && renderOption(option)))), !(filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) && (_jsx(ListItem, Object.assign({ my: 1, p: 2 }, { children: _jsx(Flex, Object.assign({ align: "center" }, { children: notFoundText })) })))] })))] })));
});
AutoSuggest.displayName = "AutoSuggest";
export default AutoSuggest;
//# sourceMappingURL=AutoSuggest.js.map